/**
 * WEBSOCKET / SIGNALR ADMIN SERVICE: FIGHTERS
 * Wrapper de comunicación en tiempo real para el Panel del Operador / Administrador del Estadio.
 * Gestiona la conexión persistente con SignalR Hub para recibir telemetría de combate,
 * eventos de combos, transformaciones de baraja y cambios de estado sincronizados.
 * 
 * @module utilities/WS/Admin/FightersWS
 */

import SignalRService from '../SignalRService';

/**
 * Referencia al handler de escucha registrado en SignalRService para mensajes genéricos.
 * @type {Function|null}
 */
let registeredMessageHandler = null;

/**
 * Referencia al handler de reconexión registrado en SignalRService.
 * @type {Function|null}
 */
let registeredReconnectedHandler = null;

/**
 * Referencia al handler de desconexión o reconexión en progreso.
 * @type {Function|null}
 */
let registeredReconnectingHandler = null;

/**
 * Referencia al handler de cierre definitivo de conexión.
 * @type {Function|null}
 */
let registeredCloseHandler = null;

/**
 * Conecta el panel de administración al Hub de SignalR con el rol de operador de Fighters ('fighters:admin').
 * @param {Function} [onIncomingMessage=() => {}] - Callback para procesar los mensajes y eventos de telemetría emitidos por el Hub.
 * @param {Function} [onStatusChange=() => {}] - Callback para notificar cambios de estado ('connecting', 'connected', 'reconnecting', 'error', 'closed').
 * @param {string} [roomId=''] - Identificador GUID de la sala para filtrar el tráfico de eventos.
 */
export const ConnectWebSocketForFightersAdmin = (
    onIncomingMessage = () => {},
    onStatusChange = () => {},
    roomId = ''
) => {
    onStatusChange('connecting');

    // Desuscribir handlers previos si existieran para evitar fugas de memoria o ejecuciones duplicadas
    if (registeredMessageHandler) {
        SignalRService.off('message', registeredMessageHandler);
    }
    if (registeredReconnectedHandler) {
        SignalRService.off('reconnected', registeredReconnectedHandler);
    }
    if (registeredReconnectingHandler) {
        SignalRService.off('reconnecting', registeredReconnectingHandler);
    }
    if (registeredCloseHandler) {
        SignalRService.off('close', registeredCloseHandler);
    }

    // Handler de mensajes entrantes con filtrado estricto por sala si fue provista
    registeredMessageHandler = (messageEnvelope) => {
        if (!messageEnvelope) return;

        const envelopeRoomId = messageEnvelope.roomId
            || messageEnvelope.RoomId
            || messageEnvelope.payload?.roomId
            || messageEnvelope.payload?.RoomId
            || messageEnvelope.data?.roomId
            || messageEnvelope.data?.RoomId;

        if (roomId && envelopeRoomId && String(envelopeRoomId).toLowerCase() !== String(roomId).toLowerCase()) {
            return;
        }

        if (typeof onIncomingMessage === 'function') {
            onIncomingMessage(messageEnvelope);
        }
    };

    registeredReconnectedHandler = () => {
        onStatusChange('connected');
    };

    registeredReconnectingHandler = () => {
        onStatusChange('reconnecting');
    };

    registeredCloseHandler = () => {
        onStatusChange('closed');
    };

    SignalRService.on('message', registeredMessageHandler);
    SignalRService.on('reconnected', registeredReconnectedHandler);
    SignalRService.on('reconnecting', registeredReconnectingHandler);
    SignalRService.on('close', registeredCloseHandler);

    SignalRService.connect({
        tipo: 'fighters:admin',
        roomId: roomId || ''
    })
        .then(() => {
            onStatusChange('connected');
        })
        .catch((connectionError) => {
            console.error('[FightersAdminWS] Error al establecer conexión con el Hub de SignalR:', connectionError);
            onStatusChange('error');
        });
};

/**
 * Desconecta limpiamente la sesión SignalR del operador de Fighters y remueve los escuchas registrados.
 */
export const DisconnectWebSocketForFightersAdmin = () => {
    try {
        if (registeredMessageHandler) {
            SignalRService.off('message', registeredMessageHandler);
            registeredMessageHandler = null;
        }
        if (registeredReconnectedHandler) {
            SignalRService.off('reconnected', registeredReconnectedHandler);
            registeredReconnectedHandler = null;
        }
        if (registeredReconnectingHandler) {
            SignalRService.off('reconnecting', registeredReconnectingHandler);
            registeredReconnectingHandler = null;
        }
        if (registeredCloseHandler) {
            SignalRService.off('close', registeredCloseHandler);
            registeredCloseHandler = null;
        }
        SignalRService.disconnect();
    } catch (disconnectionError) {
        console.error('[FightersAdminWS] Error al desconectar la sesión de SignalR del operador:', disconnectionError);
    }
};

export default {
    ConnectWebSocketForFightersAdmin,
    DisconnectWebSocketForFightersAdmin
};
